import type { ComponentProps, JSX, MouseEvent } from 'react'

import { Button, type ButtonProps } from './button.tsx'
import { isPlainClick } from '../lib/click.ts'

/**
 * The `onClick` a routed anchor wears.
 *
 * One function rather than one per component, because the rule it applies is
 * the whole point of this file. A link that wrongly cancels a modified click
 * still looks correct on a plain click, so a hand-written copy of this can be
 * wrong for a long time before anyone notices.
 *
 * @param onNavigate - Routes the app, for the clicks the app answers.
 * @returns The click handler.
 */
function routedClick(
  onNavigate: () => void
): (event: MouseEvent<HTMLAnchorElement>) => void {
  return event => {
    // Anything but a plain click is a request aimed at the BROWSER — a new
    // tab, a new window — and the `href` is what answers it. Leave the event
    // completely alone in that case.
    if (!isPlainClick(event)) {
      return
    }

    // A plain click is ours: suppress the full page load and route in place.
    event.preventDefault()
    onNavigate()
  }
}

/**
 * How a text link is drawn.
 *
 * Opt-in rather than `Link`'s default, because not every link wants it: nav
 * chrome would regress with an underline on hover. It lives here rather than
 * beside its first caller because the string describes a LINK — a second
 * hand-written copy drifts the moment the focus ring is revisited, and the
 * copy that drifts is the one nobody is looking at.
 *
 * The global outline owns focus; this class must add no glow.
 */
export const LINK_CLASS =
  'text-brand-ink underline-offset-4 hover:underline focus-visible:ring-0'

export type LinkProps = Omit<ComponentProps<'a'>, 'href' | 'onClick'> & {
  href: string
  'data-testid': string
  /** Routes the app in place. Called only for a plain click. */
  onNavigate: () => void
}

export type ButtonLinkProps = LinkProps & Pick<ButtonProps, 'variant'>

/**
 * Link — a destination the router answers in place.
 *
 * **A real `<a href>`, always.** The element is what carries the behaviour:
 * only a real `href` lets the reader middle click for a new tab, hold a
 * modifier for the same, copy the address from the context menu, see the URL
 * on hover, or drag the link to the bookmark bar. A screen reader also
 * announces it as a link, which is what it is.
 *
 * Routing stays injected through `onNavigate` rather than read from a router
 * hook, so a screen using this still renders in a test with no router around
 * it. That is a hard requirement for `NavRail` and `ConsoleShell`.
 *
 * @param props - Link props, and any other anchor attribute.
 * @param props.href - Where the link goes. Required, and never `#`.
 * @param props.onNavigate - Routes in place on a plain click.
 * @param props.data-testid - Test hook. Required, so a missing one is a type
 *   error rather than a lint error.
 * @returns The rendered anchor.
 */
export function Link({
  href,
  onNavigate,
  'data-testid': testId,
  ...props
}: LinkProps): JSX.Element {
  // `href`, the hook and the handler are pinned AFTER the spread: they are
  // this component's contract and a caller may not overwrite one. The hook is
  // a literal attribute for a second reason — the locator lint rule reads the
  // JSX and not the rendered DOM, so one arriving in a spread does not count.
  return (
    <a
      {...props}
      href={href}
      data-testid={testId}
      onClick={routedClick(onNavigate)}
    />
  )
}

/**
 * ButtonLink — a destination that looks like a button.
 *
 * Every link behaviour of `Link`, wearing a `Button`'s variant. Use it where
 * the destination is the screen's action, such as the way back out of a
 * detail screen; use `Link` where it is text.
 *
 * `asChild` is handled here so that no feature file writes it by hand.
 *
 * @param props - `Link` props, plus the button's variant.
 * @param props.variant - Domain variant, passed to `Button`.
 * @param props.className - Caller classes, composed with the button's.
 * @returns The rendered anchor, styled as a button.
 */
export function ButtonLink({
  href,
  onNavigate,
  'data-testid': testId,
  variant,
  className,
  ...props
}: ButtonLinkProps): JSX.Element {
  return (
    <Button
      data-testid={testId}
      variant={variant}
      className={className}
      asChild
    >
      {/*
        A bare anchor rather than `Link`, because `asChild` merges the button's
        props onto it: the DOM carries the hook once, and writing it here too
        would put the same string in the source twice. The click rule is
        shared through `routedClick` instead of being copied.
      */}
      <a {...props} href={href} onClick={routedClick(onNavigate)} />
    </Button>
  )
}
